import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Clock, Check, X, Play, Pause } from "lucide-react";
import useAudition from "@/hooks/useAudition";

/**
 * Inline "Preview Start" picker on each track row in Studio.
 * Collapsed = a small "HOOK: 0:42" button. Expanded = a live audition panel
 * that plays the exact 30-second window fans will hear.
 *
 * State lives in the parent so cancel restores the original value. Audio
 * lifecycle is owned by `useAudition` (native <audio> — doesn't fight the
 * app's persistent player).
 */
const CLIP_SEC = 30;

function fmt(sec) {
  const s = Math.max(0, Math.floor(sec || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

export default function PreviewStartPicker({ track, onSaved }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(track.preview_start_sec || 0);
  const [saving, setSaving] = useState(false);

  if (!open) {
    return (
      <button
        data-testid={`preview-start-btn-${track.id}`}
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-gray-400 hover:text-[#FF4500] whitespace-nowrap transition-colors"
        title="Pick where the 30s preview begins"
      >
        <Clock className="h-3 w-3" />
        Hook: {fmt(track.preview_start_sec || 0)}
      </button>
    );
  }

  return (
    <ExpandedPicker
      track={track}
      value={value}
      setValue={setValue}
      saving={saving}
      setSaving={setSaving}
      onCancel={() => { setValue(track.preview_start_sec || 0); setOpen(false); }}
      onSaved={(v) => { onSaved?.(v); setOpen(false); }}
    />
  );
}

/** Live-audition panel — kept as its own component so the hook doesn't
 *  spin up until the artist actually opens the picker. */
function ExpandedPicker({ track, value, setValue, saving, setSaving, onCancel, onSaved }) {
  const audition = useAudition(track.audio_path ? fileUrl(track.audio_path) : "", value, CLIP_SEC);
  const maxStart = Math.max(0, (track.duration_sec || 300) - CLIP_SEC);
  const pct = Math.min(100, (audition.elapsed / CLIP_SEC) * 100);

  // Stop the audition when the picker closes.
  useEffect(() => () => audition.stop(), []);  // eslint-disable-line

  const onSlider = (v) => { setValue(v); audition.seek(v); };

  const save = async () => {
    audition.stop();
    setSaving(true);
    try {
      const { data } = await api.patch(`/tracks/${track.id}/preview-start`, { preview_start_sec: value });
      toast.success(`Preview starts at ${fmt(data.preview_start_sec)}`);
      onSaved(data.preview_start_sec);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div
      data-testid={`preview-start-picker-${track.id}`}
      className="flex items-center gap-2 bg-black/40 border border-[#FF4500]/40 px-2 py-1.5 flex-wrap"
    >
      <button
        data-testid={`preview-start-audition-${track.id}`}
        onClick={audition.playing ? audition.stop : audition.play}
        title={audition.playing ? "Stop audition" : "Hear the 30s preview from this spot"}
        className="h-7 w-7 grid place-items-center bg-[#FF4500] hover:bg-[#E53E00] text-black transition-colors shrink-0"
      >
        {audition.playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5 ml-0.5" />}
      </button>

      <div className="flex flex-col gap-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400 whitespace-nowrap">Start:</span>
          <input
            data-testid={`preview-start-slider-${track.id}`}
            type="range"
            min={0}
            max={maxStart}
            step={1}
            value={value}
            onChange={(e) => onSlider(Number(e.target.value))}
            className="w-24 sm:w-32 accent-[#FF4500]"
          />
          <span className="font-mono text-xs text-[#FF4500] tabular-nums w-10 text-center">{fmt(value)}</span>
        </div>
        <div className="relative h-1 bg-white/10 overflow-hidden">
          <div
            className="absolute inset-y-0 left-0 bg-[#FF4500] transition-[width] duration-100"
            style={{ width: `${pct}%` }}
            data-testid={`preview-start-progress-${track.id}`}
          />
        </div>
        <div className="font-mono text-[9px] uppercase tracking-widest text-gray-500">
          {audition.playing
            ? `${audition.elapsed.toFixed(0)}s / ${CLIP_SEC}s`
            : `Plays ${fmt(value)}–${fmt(value + CLIP_SEC)}`}
        </div>
      </div>

      <button
        data-testid={`preview-start-save-${track.id}`}
        onClick={save}
        disabled={saving}
        className="h-7 w-7 grid place-items-center bg-[#FF4500] hover:bg-[#E53E00] disabled:bg-gray-600 text-black transition-colors shrink-0"
        title="Save"
      >
        <Check className="h-3.5 w-3.5" />
      </button>
      <button
        onClick={() => { audition.stop(); onCancel(); }}
        className="h-7 w-7 grid place-items-center border border-[#2A2A2A] hover:border-white/40 text-gray-400 shrink-0"
        title="Cancel"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}
